import { PlusIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { useEffect, useState } from 'react';

import TextInput from '@/components/Forms/TextInput';
import { ClientButton } from '@/components/UI/Button';
import useRequest from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';

import UpdateContactsForm from './UpdateContactsForm';

type Slot = 'contactSecondary' | 'contactTertiary';

// Clearing an alternate address is a plain update with an empty value (no code
// needed) — mirrors the removal path inside UpdateContactsForm.
const removeContactRequest: RequestSchema<
  API.WithData<API.User>,
  API.WithData<{ contactSecondary?: string; contactTertiary?: string }>
> = {
  name: 'removeExtraEmail',
  input: '/api/v1/user/update/me?v2Verify=true',
  method: 'POST',
};

/**
 * A saved (confirmed) extra recipient, shown read-only with a quiet Remove link
 * — matching the primary email's read-only + Edit treatment. There's no in-place
 * edit: to change an address you remove it and add a new one (which re-verifies).
 */
function SavedEmailRow({
  email,
  onRemove,
  loading,
}: {
  email: string;
  onRemove: () => void;
  loading: boolean;
}) {
  return (
    <div className="flex items-center gap-3">
      <TextInput
        readOnly
        value={email}
        aria-label="Extra email recipient"
        className="max-w-sm cursor-default bg-zinc-50 text-zinc-600 dark:bg-white/5 dark:text-zinc-300"
      />
      <ClientButton
        type="button"
        variant="link"
        size="baseline"
        onClick={onRemove}
        disabled={loading}
        loading={loading}
        className="inline-flex items-center gap-1 p-0 font-medium text-zinc-500 no-underline hover:text-red-600 hover:no-underline dark:text-zinc-400 dark:hover:text-red-400"
      >
        <XMarkIcon className="size-4" aria-hidden="true" />
        Remove
      </ClientButton>
    </div>
  );
}

/**
 * The "Extra email recipients" control (up to two). The first slot always shows
 * inline; the second is revealed by a single "+ Add another email" link so we
 * never show two empty inputs. A saved address renders read-only with a Remove
 * link; an empty or pending (unverified) slot renders the full add/verify form.
 * SMS marketing numbers have their own Setting (LegacyPhonesOnFile), not here.
 */
export default function ExtraEmailRecipients({
  user,
  setUser,
}: {
  user: API.User;
  setUser: (newUser: API.User | null) => void;
}) {
  const [addingTertiary, setAddingTertiary] = useState(false);
  const removeReq = useRequest(removeContactRequest, { manual: true });

  const isSaved = (slot: Slot) => !!user?.[slot] && !user?.meta?.[slot];
  const isPending = (slot: Slot) => !!user?.meta?.[slot];

  const savedTertiary = isSaved('contactTertiary');
  const pendingTertiary = isPending('contactTertiary');
  const showTertiary = savedTertiary || pendingTertiary || addingTertiary;

  // Once the second slot gains a value, collapse the standalone add affordance.
  useEffect(() => {
    if (addingTertiary && (savedTertiary || pendingTertiary)) {
      setAddingTertiary(false);
    }
  }, [addingTertiary, savedTertiary, pendingTertiary]);

  const remove = async (slot: Slot) => {
    const res = await removeReq.run(
      { data: { [slot]: '' } as { contactSecondary?: string; contactTertiary?: string } },
      { throwOnRun: true },
    );
    if (res) {
      setUser(res as unknown as API.User);
    }
  };

  return (
    <div className="space-y-5">
      {/* First slot: read-only when saved, otherwise an inline add/verify form. */}
      {isSaved('contactSecondary') ? (
        <SavedEmailRow
          email={user.contactSecondary ?? ''}
          onRemove={() => remove('contactSecondary')}
          loading={removeReq.loading}
        />
      ) : (
        <UpdateContactsForm user={user} setUser={setUser} name="contactSecondary" secondary />
      )}

      {/* Second slot: progressive — read-only when saved, form while adding. */}
      {showTertiary &&
        (savedTertiary ? (
          <SavedEmailRow
            email={user.contactTertiary ?? ''}
            onRemove={() => remove('contactTertiary')}
            loading={removeReq.loading}
          />
        ) : (
          <UpdateContactsForm
            user={user}
            setUser={setUser}
            name="contactTertiary"
            secondary
            onCancelAdd={() => setAddingTertiary(false)}
          />
        ))}

      {!showTertiary && (
        <button
          type="button"
          onClick={() => setAddingTertiary(true)}
          className="inline-flex items-center gap-1 font-semibold text-[#15734A] text-sm hover:underline dark:text-[#34B473]"
        >
          <PlusIcon className="size-4" aria-hidden="true" />
          Add another email
        </button>
      )}
    </div>
  );
}
